import { GQtyError } from "gqty";
import { getDefaultStore } from "jotai";
import { z } from "zod";
import actAsUserAtom, {
  parseActAsSession,
  storageKey,
  type ActAsSession,
} from "./uiState/actAsUserAtom";
import { getApiUrl } from "lib/apiUrl";
import Toaster from "./Toaster";

export const ACT_AS_IDLE_MS = 5 * 60_000;
export const ACT_AS_WARNING_MS = 4 * 60_000;
const HEARTBEAT_MS = 30_000;
const responseSchema = z.object({
  token: z.string().min(1),
  userId: z.string().min(1),
  userName: z.string(),
  expiresAt: z.number().finite().positive(),
  idleTimeoutSeconds: z.literal(300),
});
const renewalSchema = z
  .object({
    jti: z.string().min(1),
    expiresAt: z.number().finite().positive(),
  })
  .transform(({ jti, expiresAt }) => ({ sessionId: jti, expiresAt }));

function readTokenRenewal(token: string) {
  try {
    const payload = token.split(".")[1];
    if (!payload) throw new Error("Missing token payload.");
    return renewalSchema.parse(
      JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))),
    );
  } catch {
    throw new ActAsRequestError("Invalid act-as renewal response.");
  }
}
const store = getDefaultStore();
const cacheClearers = new Set<() => void>();
let generation = 0;
let observedIdentity = sessionIdentity(store.get(actAsUserAtom));
let pendingHeartbeat: { sessionId: string; promise: Promise<void> } | undefined;
let lastHeartbeatActivity = store.get(actAsUserAtom)?.lastActivity ?? 0;
let lastHeartbeatAt = Date.now();

export class ActAsRequestError extends GQtyError {}

export class ActAsPinError extends Error {
  constructor() {
    super("Incorrect PIN.");
  }
}

export class ActAsSessionEndedError extends ActAsRequestError {
  constructor() {
    super("User session ended. Switch to the user again.");
  }
}

export function registerActAsCacheClearer(clear: () => void) {
  cacheClearers.add(clear);
  return () => {
    cacheClearers.delete(clear);
  };
}
function clearCaches() {
  for (const clear of cacheClearers) clear();
}

function sessionIdentity(session: ActAsSession | null) {
  return session
    ? JSON.stringify([session.sessionId, session.requesterUid, session.actAsUserId])
    : null;
}

// Token renewal preserves identity; only starting or ending a session clears caches.
store.sub(actAsUserAtom, () => {
  const identity = sessionIdentity(store.get(actAsUserAtom));
  if (identity !== observedIdentity) {
    observedIdentity = identity;
    generation++;
    clearCaches();
  }
});

async function authenticatedRequest(
  path: string,
  method: string,
  body: unknown,
) {
  const { getAuth } = await import("firebase/auth");
  const { app } = await import("./Firebase");
  const user = getAuth(app).currentUser;
  if (!user) throw new ActAsSessionEndedError();
  const token = await user.getIdToken();
  const response = await fetch(getApiUrl(path), {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  return { response, requesterUid: user.uid };
}

// Read storage before asynchronous results are applied: another tab may have ended the session.
export function currentActAsSession(): ActAsSession | null {
  let saved: ActAsSession | null = null;
  try {
    saved = parseActAsSession(
      JSON.parse(localStorage.getItem(storageKey) ?? "null"),
    );
  } catch {
    /* Invalid storage is cleared below. */
  }
  const current = store.get(actAsUserAtom);
  if (!saved && localStorage.getItem(storageKey) !== null)
    localStorage.removeItem(storageKey);
  if (JSON.stringify(saved) !== JSON.stringify(current)) {
    store.set(actAsUserAtom, saved);
  }
  return saved;
}

export const ACT_AS_PIN_NOT_ASSIGNED_MESSAGE =
  "This user must have a PIN assigned before you can act as them.";

export async function startActAsSession(userId: string, pin?: string) {
  const previous = currentActAsSession();
  const requestGeneration = generation;
  const { response, requesterUid } = await authenticatedRequest(
    "/api/act-as/session",
    "POST",
    pin === undefined ? { userId } : { userId, pin },
  );
  if (!response.ok) {
    if (response.status === 403) {
      const error = z.object({ code: z.string() }).safeParse(
        await response.json().catch(() => null),
      );
      if (error.success && error.data.code === "ACT_AS_PIN_INVALID") {
        throw new ActAsPinError();
      }
      if (error.success && error.data.code === "ACT_AS_PIN_NOT_ASSIGNED") {
        throw new Error(ACT_AS_PIN_NOT_ASSIGNED_MESSAGE);
      }
    }
    throw new Error("Unable to switch user. You may not have permission to switch to this user.");
  }
  const result = responseSchema.parse(await response.json());
  const { sessionId } = readTokenRenewal(result.token);
  const { getAuth } = await import("firebase/auth");
  const { app } = await import("./Firebase");
  if (
    requestGeneration !== generation ||
    currentActAsSession()?.sessionId !== previous?.sessionId ||
    getAuth(app).currentUser?.uid !== requesterUid
  ) {
    throw new ActAsSessionEndedError();
  }
  generation++;
  lastHeartbeatAt = Date.now();
  lastHeartbeatActivity = lastHeartbeatAt;
  const session: ActAsSession = {
    sessionId,
    token: result.token,
    actAsUserId: result.userId,
    actAsUserName: result.userName,
    requesterUid,
    lastActivity: Date.now(),
    expiresAt: result.expiresAt,
  };
  store.set(actAsUserAtom, session);
  return session;
}

export async function endActAsSession() {
  const hadSession = observedIdentity !== null;
  generation++;
  store.set(actAsUserAtom, null);
  localStorage.removeItem(storageKey);
  // Base-account sign-out also resets caches; switched sessions already reset via the atom.
  if (!hadSession) clearCaches();
}

export async function signOutCurrentUser() {
  const actingAsUser = currentActAsSession();
  await endActAsSession();
  if (actingAsUser) {
    Toaster.info(`${actingAsUser.actAsUserName} was signed out.`);
  } else {
    const { getAuth } = await import("firebase/auth");
    const { app } = await import("./Firebase");
    await getAuth(app).signOut();
  }
}

export async function heartbeatActAsSession(
  force = false,
  explicitContinue = false,
) {
  const session = currentActAsSession();
  if (!session) return;
  if (Date.now() - session.lastActivity >= ACT_AS_IDLE_MS) {
    void endActAsSession();
    throw new ActAsSessionEndedError();
  }
  if (pendingHeartbeat?.sessionId === session.sessionId) {
    await pendingHeartbeat.promise;
    if (explicitContinue) return heartbeatActAsSession(true, true);
    return;
  }
  if (
    !force &&
    (lastHeartbeatActivity >= session.lastActivity ||
      Date.now() - lastHeartbeatAt < HEARTBEAT_MS)
  )
    return;
  const requestGeneration = generation;
  const activityAt = explicitContinue ? Date.now() : session.lastActivity;
  const promise = (async () => {
    const { response } = await authenticatedRequest(
      "/api/act-as/heartbeat",
      "POST",
      {
        token: session.token,
        activityAgeSeconds: Math.max(0, (Date.now() - activityAt) / 1000),
      },
    );
    let current = currentActAsSession();
    if (
      requestGeneration !== generation ||
      current?.sessionId !== session.sessionId
    )
      throw new ActAsSessionEndedError();
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        void endActAsSession();
        throw new ActAsSessionEndedError();
      }
      throw new Error("Unable to renew the act-as session.");
    }
    const result = responseSchema.parse(await response.json());
    const { sessionId } = readTokenRenewal(result.token);
    current = currentActAsSession();
    if (
      requestGeneration !== generation ||
      current?.sessionId !== session.sessionId
    )
      throw new ActAsSessionEndedError();
    if (
      sessionId !== session.sessionId ||
      result.userId !== current.actAsUserId
    )
      throw new ActAsRequestError("Invalid act-as renewal response.");
    if (Date.now() - current.lastActivity >= ACT_AS_IDLE_MS) {
      void endActAsSession();
      throw new ActAsSessionEndedError();
    }
    store.set(actAsUserAtom, {
      ...current,
      lastActivity: Math.max(current.lastActivity, activityAt),
      token: result.expiresAt >= current.expiresAt ? result.token : current.token,
      expiresAt: Math.max(result.expiresAt, current.expiresAt),
    });
    lastHeartbeatAt = Date.now();
    lastHeartbeatActivity = Math.max(lastHeartbeatActivity, activityAt);
  })();
  pendingHeartbeat = { sessionId: session.sessionId, promise };
  try {
    await promise;
  } finally {
    if (pendingHeartbeat?.promise === promise) pendingHeartbeat = undefined;
  }
}

export function recordActAsActivity() {
  const session = currentActAsSession();
  if (!session) return;
  const age = Date.now() - session.lastActivity;
  if (age >= ACT_AS_IDLE_MS) {
    void endActAsSession();
    return;
  }
  if (age >= ACT_AS_WARNING_MS || age < 1000) return;
  store.set(actAsUserAtom, { ...session, lastActivity: Date.now() });
}

export function captureActAsRequest() {
  const session = currentActAsSession();
  return {
    token: session?.token ?? null,
    sessionId: session?.sessionId ?? null,
    generation,
  };
}

export function assertActAsRequestCurrent(request: {
  token: string | null;
  sessionId: string | null;
  generation: number;
}) {
  const current = currentActAsSession();
  if (
    request.generation !== generation ||
    (current?.sessionId ?? null) !== request.sessionId
  )
    throw new ActAsSessionEndedError();
  if (current && Date.now() - current.lastActivity >= ACT_AS_IDLE_MS) {
    void endActAsSession();
    throw new ActAsSessionEndedError();
  }
}

export async function prepareActAsRequest(requesterUid: string | undefined) {
  const session = currentActAsSession();
  if (!session) return { token: null, sessionId: null, generation };
  if (
    session.requesterUid !== requesterUid ||
    Date.now() - session.lastActivity >= ACT_AS_IDLE_MS
  ) {
    void endActAsSession();
    throw new ActAsSessionEndedError();
  }
  if (session.expiresAt <= Date.now()) await heartbeatActAsSession(true);
  const current = currentActAsSession();
  if (!current || current.expiresAt <= Date.now())
    throw new ActAsSessionEndedError();
  return { token: current.token, sessionId: current.sessionId, generation };
}

export async function guardActAsResponse(
  response: Response,
  request: ReturnType<typeof captureActAsRequest>,
) {
  assertActAsRequestCurrent(request);
  if (request.token && (response.status === 401 || response.status === 403)) {
    // Ordinary permission-denied responses must not end an otherwise valid session.
    const error = await response
      .clone()
      .json()
      .catch(() => null);
    assertActAsRequestCurrent(request);
    if (response.status === 401 || error?.code?.startsWith("ACT_AS_")) {
      const current = currentActAsSession();
      // A concurrent renewal may already have replaced an expired request token.
      if (
        error?.code !== "ACT_AS_SESSION_EXPIRED" ||
        current?.token === request.token ||
        !current ||
        current.expiresAt <= Date.now()
      )
        void endActAsSession();
      throw new ActAsSessionEndedError();
    }
  }
}

export function subscribeActAsActivity(onTick: () => void) {
  const activity = (event: Event) => {
    if (event.isTrusted) recordActAsActivity();
  };
  const events = [
    "pointerdown",
    "pointermove",
    "keydown",
    "scroll",
    "touchstart",
    "wheel",
  ];
  for (const event of events)
    window.addEventListener(event, activity, { capture: true, passive: true });
  const tick = () => {
    const session = currentActAsSession();
    if (session) {
      if (Date.now() - session.lastActivity >= ACT_AS_IDLE_MS)
        void endActAsSession();
      else void heartbeatActAsSession().catch(() => undefined);
    }
    onTick();
  };
  const timer = window.setInterval(tick, 1000);
  const sync = () => {
    currentActAsSession();
    tick();
  };
  window.addEventListener("storage", sync);
  window.addEventListener("focus", tick);
  document.addEventListener("visibilitychange", tick);
  tick();
  return () => {
    window.clearInterval(timer);
    for (const event of events)
      window.removeEventListener(event, activity, true);
    window.removeEventListener("storage", sync);
    window.removeEventListener("focus", tick);
    document.removeEventListener("visibilitychange", tick);
  };
}

// All app transports share identity guards; a pending write never falls back to the base user.
export async function actAsFetch(input: RequestInfo | URL, init?: RequestInit) {
  const beforeAuth = captureActAsRequest();
  try {
    const { getAuth } = await import("firebase/auth");
    const { app } = await import("./Firebase");
    assertActAsRequestCurrent(beforeAuth);
    const user = getAuth(app).currentUser;
    const request = await prepareActAsRequest(user?.uid);
    const idToken = await user?.getIdToken();
    assertActAsRequestCurrent(request);
    if (getAuth(app).currentUser?.uid !== user?.uid)
      throw new ActAsSessionEndedError();
    const headers = new Headers(init?.headers);
    headers.set("Authorization", idToken ? `Bearer ${idToken}` : "");
    headers.delete("X-Act-As-User");
    headers.delete("X-Act-As-Token");
    if (request.token) headers.set("X-Act-As-Token", request.token);
    const response = await fetch(input, { ...init, headers });
    await guardActAsResponse(response, request);
    // Await the body as well, so a slow response cannot be read after an identity switch.
    await response.clone().text();
    assertActAsRequestCurrent(request);
    if (request.token && response.ok) {
      const token = response.headers.get("X-Act-As-Token");
      const current = currentActAsSession();
      if (token && current) {
        const { expiresAt, sessionId } = readTokenRenewal(token);
        if (sessionId !== current.sessionId || expiresAt <= Date.now())
          throw new ActAsRequestError("Invalid act-as renewal response.");
        if (expiresAt >= current.expiresAt)
          store.set(actAsUserAtom, {
            ...current,
            token,
            expiresAt,
            lastActivity: Math.max(current.lastActivity, expiresAt - ACT_AS_IDLE_MS),
          });
        lastHeartbeatAt = Date.now();
        lastHeartbeatActivity = Math.max(
          lastHeartbeatActivity,
          expiresAt - ACT_AS_IDLE_MS,
        );
      }
    }
    if (request.token && !response.ok)
      throw new ActAsRequestError("Act-as request failed. Please try again.");
    return response;
  } catch (error) {
    // GQty retries unknown failures, including mutations. An act-as write must
    // never retry later after Stop under the original logged-in user.
    if (beforeAuth.token && !(error instanceof ActAsRequestError)) {
      throw new ActAsRequestError(
        error instanceof Error ? error.message : "Act-as request failed.",
        { otherError: error },
      );
    }
    throw error;
  }
}
